import { IAuthDataSource } from '../../types/features/auth/auth-types';
import { ManifestData } from '../../types/features/auth/auth-contracts';
import { ResponseStatus } from '../../../../shared/infrastructure/types/api/api-types';

const MANIFEST_RETRY_DELAY_MS = 200;
const ASSET_ERROR_DELAY_MS = 200;

class Loader {
  private servers: Url[];
  private manifestPath: string;
  private assetsPath = '';
  private currentServer = 0;

  private datasource: IAuthDataSource;

  constructor(servers: Url[], manifestPath: string, datasource: IAuthDataSource) {
    this.servers = servers;
    this.manifestPath = manifestPath;
    this.datasource = datasource;
  }

  async load(onManifest?: (manifest: ManifestData) => void): Promise<ManifestData> {
    const manifest = await this.loadManifest();

    this.assetsPath = manifest.assets_path;
    onManifest?.(manifest);

    await Promise.all([this.loadAssets(manifest.css), this.loadAssets(manifest.js)]);

    return manifest;
  }

  private async loadManifest(): Promise<ManifestData> {
    if (this.isExternalManifest()) {
      const response = await this.datasource.getManifest(
        `${this.manifestPath}?ts=${Date.now()}` as Url,
      );

      if (response.status === ResponseStatus.SUCCESS) {
        return response.result;
      }

      throw new Error('Failed to load manifest');
    }

    for (let i = 0; i < this.servers.length; i++) {
      this.currentServer = i;

      const response = await this.datasource.getManifest(
        `${this.servers[i]}${this.manifestPath}?ts=${Date.now()}` as Url,
      );

      if (response.status === ResponseStatus.SUCCESS && Object.keys(response.result).length > 0) {
        return response.result;
      }

      if (i < this.servers.length - 1) {
        await this.delay(MANIFEST_RETRY_DELAY_MS);
      }
    }

    throw new Error('Failed to load manifest');
  }

  private async loadAssets(assets: string[]): Promise<void> {
    for (const asset of assets) {
      await this.loadAsset(asset);
    }
  }

  private async loadAsset(asset: string): Promise<void> {
    try {
      await this.injectAsset(this.getURL(this.servers[this.currentServer], asset));
      return;
    } catch {
      /* TODO нужна ли тут попытка на другие app-servers? */
    }

    for (let offset = 1; offset < this.servers.length; offset++) {
      const index = (this.currentServer + offset) % this.servers.length;

      try {
        this.currentServer = index;
        await this.injectAsset(this.getURL(this.servers[index], asset));
        return;
      } catch {
        if (offset === this.servers.length - 1) {
          throw new Error(`Failed to load asset: ${asset}`);
        }
      }
    }
  }

  private injectAsset(url: string): Promise<void> {
    if (url.endsWith('.js')) return this.injectScript(url);
    if (url.endsWith('.css')) return this.injectStylesheet(url);

    return Promise.reject(new Error(`Unknown asset type: ${url}`));
  }

  private injectScript(url: string): Promise<void> {
    const script = document.createElement('script');

    script.type = 'text/javascript';
    script.src = url;
    script.setAttribute('charset', 'utf-8');

    return this.appendToHead(script);
  }

  private injectStylesheet(url: string): Promise<void> {
    const link = document.createElement('link');

    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = url;

    return this.appendToHead(link);
  }

  private appendToHead(element: HTMLScriptElement | HTMLLinkElement): Promise<void> {
    return new Promise((resolve, reject) => {
      const head = document.getElementsByTagName('head')[0] || document.documentElement;

      element.onload = () => resolve();
      element.onerror = () => {
        setTimeout(() => {
          head.removeChild(element);
          reject();
        }, ASSET_ERROR_DELAY_MS);
      };

      head.appendChild(element);
    });
  }

  private getURL(server: string, asset: string): string {
    let baseServer = server;

    if (this.isExternalManifest()) {
      const match = this.manifestPath.match(/https?:\/\/[^/]+/);
      if (match) baseServer = match[0];
    }

    const trimmedServer = baseServer.replace(/\/+$/, '');
    const trimmedAsset = `${this.assetsPath}/${asset}`.replace(/\/\//g, '/').replace(/^\/+/, '');

    return `${trimmedServer}/${trimmedAsset}`;
  }

  private isExternalManifest(): boolean {
    return this.manifestPath.startsWith('https://');
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export { Loader };
